<template>
  <div class="page-main">
    <div class="page-top">
      <div class="flex flex-y-center">
        <span class="title">生产平面图</span>
        <span class="c-gray m-l-10">车间→产线→设备/班组 三级布局。拖动色块移动，拖右下角调整大小；产线拖到别的车间即改归属；用 +/- 整图缩放。</span>
      </div>
      <div class="flex flex-y-center">
        <el-button-group class="m-l-10 m-r-10">
          <el-button size="mini" @click="zoomOut">－</el-button>
          <el-button size="mini" @click="zoomReset">{{ Math.round(zoom * 100) }}%</el-button>
          <el-button size="mini" @click="zoomIn">＋</el-button>
        </el-button-group>
        <span class="legend m-r-15">
          <span class="legend-item"><i class="legend-dot" style="background: #409eff"></i>产线</span>
          <span class="legend-item"><i class="legend-dot" style="background: #67c23a"></i>设备</span>
          <span class="legend-item"><i class="legend-dot" style="background: #e6a23c"></i>班组</span>
        </span>
        <el-button size="mini" @click="autoLayout">全部重排</el-button>
        <el-button size="mini" @click="reload">刷新</el-button>
        <el-button type="success" size="mini" :loading="saving" @click="saveLayout">保存布局</el-button>
      </div>
    </div>

    <div class="content">
      <div class="canvas-wrap" :style="canvasWrapStyle">
        <div class="canvas-sizer" :style="sizerStyle">
          <div class="canvas" :style="canvasStyle">
            <div
              v-for="ws in workshopList"
              :key="'ws-' + ws.id"
              class="ws-block"
              :class="{ active: selected && selected.type === 'ws' && selected.id === ws.id }"
              :style="wsStyle(ws.id)"
            >
              <div class="ws-head" @mousedown="startMove('ws', ws.id, $event)">
                <span class="ws-name">{{ ws.name }}</span>
                <span class="ws-code">{{ linesOf(ws.id).length }} 条产线</span>
              </div>
              <div class="ws-body">
                <div
                  v-for="ln in linesOf(ws.id)"
                  :key="'ln-' + ln.id"
                  class="ln-block"
                  :class="{ active: selected && selected.type === 'ln' && selected.id === ln.id }"
                  :style="lnStyle(ln.id)"
                >
                  <div class="ln-head" @mousedown="startMove('ln', ln.id, $event)">
                    <span class="ln-name">{{ ln.name }}</span>
                  </div>
                  <div class="ln-body">
                    <div
                      v-for="it in itemsOf(ln.id)"
                      :key="'it-' + it.kind + '-' + it.id"
                      class="it-box"
                      :class="[it.kind === 'eq' ? 'is-eq' : 'is-team', { active: selected && selected.type === 'it' && selected.id === it.id && selected.kind === it.kind }]"
                      :style="itStyle(it)"
                      @mousedown="startMove('it', it.id, $event, it.kind)"
                    >
                      <div class="it-name" :title="it.name">{{ it.name }}</div>
                      <div class="it-sub">{{ it.kind === 'eq' ? (it.leader || '设备') : (it.leader || '班组') }}</div>
                      <div class="resize-handle it-resize" @mousedown.stop="startResize('it', it.id, $event, it.kind)"></div>
                    </div>
                  </div>
                  <div class="resize-handle ln-resize" @mousedown.stop="startResize('ln', ln.id, $event)"></div>
                </div>
              </div>
              <div class="resize-handle ws-resize" @mousedown.stop="startResize('ws', ws.id, $event)"></div>
            </div>

            <div v-if="!workshopList.length" class="canvas-empty">暂无车间数据，请先到「车间管理 / 产线管理」创建。</div>
          </div>
        </div>
      </div>

      <div class="detail-panel">
        <template v-if="selected && selected.type === 'ws'">
          <div class="detail-title">{{ selectedWs.name }}</div>
          <div class="detail-sub">车间</div>
          <div class="detail-divider"></div>
          <div class="detail-count">产线：{{ linesOf(selectedWs.id).length }} 条</div>
          <div class="detail-list">
            <div v-for="ln in linesOf(selectedWs.id)" :key="ln.id" class="detail-row">
              <div class="dr-name">{{ ln.name }}</div>
              <div class="dr-info"><span class="c-gray">设备/班组</span><span class="dr-qty">{{ itemsOf(ln.id).length }}</span></div>
            </div>
          </div>
        </template>
        <template v-else-if="selected && selected.type === 'ln'">
          <div class="detail-title">{{ selectedLine.name }}</div>
          <div class="detail-sub">产线 · 所属车间：{{ wsName(selectedLine.workshopId) }}</div>
          <div class="detail-divider"></div>
          <div class="detail-count">设备 {{ eqCount(selectedLine.id) }} 台 / 班组 {{ teamCount(selectedLine.id) }} 个</div>
          <div class="detail-list">
            <div v-for="it in itemsOf(selectedLine.id)" :key="it.kind + it.id" class="detail-row">
              <div class="dr-name">{{ it.name }}</div>
              <div class="dr-info"><span class="c-gray">{{ it.kind === 'eq' ? '设备' : '班组' }}</span><span class="dr-qty">{{ it.leader || '-' }}</span></div>
            </div>
            <div v-if="!itemsOf(selectedLine.id).length" class="c-gray p-10">该产线暂无关联设备或班组。</div>
          </div>
        </template>
        <template v-else-if="selected && selected.type === 'it'">
          <div class="detail-title">{{ selectedItem.name }}</div>
          <div class="detail-sub">{{ selectedItem.kind === 'eq' ? '设备' : '班组' }}</div>
          <div class="detail-divider"></div>
          <div class="detail-count" v-if="selectedItem.kind === 'eq'">负责人：{{ selectedItem.leader || '-' }}</div>
          <div class="detail-count" v-else>编码：{{ selectedItem.code || '-' }}</div>
        </template>
        <template v-else>
          <div class="c-gray p-10">点击左侧色块查看详情。</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
const GRID = 10
const WS_HEAD_H = 28
const LN_HEAD_H = 24

export default {
  data() {
    return {
      saving: false,
      zoom: 1,
      workshopList: [], // {id,name}
      lineList: [], // {id,name,workshopId}
      itemList: [], // {id,kind:'eq'|'team',name,leader,code,lineId}
      wsGeo: {}, // wsId -> {x,y,w,h} 绝对
      lnGeo: {}, // lnId -> {x,y,w,h} 相对车间 body
      itGeo: {}, // 'kind-id' -> {x,y,w,h} 相对产线 body
      canvas: { w: 2000, h: 1200 },
      wrapH: 400,
      drag: null,
      selected: null
    }
  },
  computed: {
    selectedWs() {
      return this.selected && this.selected.type === 'ws' ? this.workshopList.find((w) => String(w.id) === String(this.selected.id)) : null
    },
    selectedLine() {
      return this.selected && this.selected.type === 'ln' ? this.lineList.find((l) => String(l.id) === String(this.selected.id)) : null
    },
    selectedItem() {
      if (!this.selected || this.selected.type !== 'it') return null
      return this.itemList.find((i) => i.kind === this.selected.kind && String(i.id) === String(this.selected.id))
    },
    canvasWrapStyle() {
      return { height: this.wrapH + 'px' }
    },
    sizerStyle() {
      return { width: this.canvas.w * this.zoom + 'px', height: this.canvas.h * this.zoom + 'px' }
    },
    canvasStyle() {
      return { width: this.canvas.w + 'px', height: this.canvas.h + 'px', transform: 'scale(' + this.zoom + ')', transformOrigin: '0 0' }
    }
  },
  watch: {
    zoom() {
      this.saveView()
    }
  },
  mounted() {
    this.loadView()
    this.init()
    this.calcWrapH()
    window.addEventListener('mousemove', this.onMove)
    window.addEventListener('mouseup', this.onUp)
    window.addEventListener('resize', this.calcWrapH)
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onMove)
    window.removeEventListener('mouseup', this.onUp)
    window.removeEventListener('resize', this.calcWrapH)
  },
  methods: {
    async init() {
      await this.loadData()
      await this.loadLayout()
    },
    reload() {
      this.selected = null
      this.init()
    },
    // 画布高度按自身到视口顶部的实际距离动态计算，正好填满到底部，避免外层再出现一条全局竖向滚动条
    calcWrapH() {
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.canvas-wrap')
        if (!el) return
        const top = el.getBoundingClientRect().top
        this.wrapH = Math.max(240, window.innerHeight - top - 16)
      })
    },
    loadData() {
      return this.$api({ method: 'post', url: '/admin/gcvip/productionLayout/orgData', data: {} }).then((res) => {
        const d = res.data || {}
        this.workshopList = (d.workshops || []).map((w) => ({ id: w.id, name: w.name }))
        // 归属(workshopId/lineId)由后端推断完成，前端直接消费
        this.lineList = (d.lines || []).map((l) => ({ id: l.id, name: l.name, workshopId: l.workshopId }))
        const eqItems = (d.equipment || []).map((e) => ({ id: e.id, kind: 'eq', name: e.name, leader: e.leader, lineId: e.lineId }))
        const teamItems = (d.teams || []).map((t) => ({ id: t.id, kind: 'team', name: t.name, code: t.code, leader: t.name, lineId: t.lineId }))
        this.itemList = eqItems.concat(teamItems)
      })
    },
    loadLayout() {
      return this.$api({ method: 'get', url: '/admin/gcvip/productionLayout/find?scope=org' })
        .then((res) => {
          const payload = res.data && res.data.data
          // 仅当已存布局的车间 id 与当前数据有交集时才复用（避免实体 id 体系变更后旧布局失配导致色块堆叠）
          const savedWs = (payload && payload.layout && payload.layout.workshops) || []
          const matched = savedWs.some((w) => this.workshopList.some((x) => String(x.id) === String(w.id)))
          if (payload && payload.layout && matched && savedWs.length) {
            this.canvas.w = payload.canvas_w || 2000
            this.canvas.h = payload.canvas_h || 1200
            const wsGeo = {}
            const lnGeo = {}
            const itGeo = {}
            ;(payload.layout.workshops || []).forEach((w) => (wsGeo[w.id] = { x: w.x, y: w.y, w: w.w, h: w.h }))
            ;(payload.layout.lines || []).forEach((l) => {
              lnGeo[l.id] = { x: l.x, y: l.y, w: l.w, h: l.h }
              const line = this.lineList.find((x) => String(x.id) === String(l.id))
              if (line && l.workshopId != null) line.workshopId = l.workshopId
            })
            ;(payload.layout.equipment || []).forEach((e) => {
              itGeo['eq-' + e.id] = { x: e.x, y: e.y, w: e.w, h: e.h }
              const it = this.itemList.find((x) => x.kind === 'eq' && String(x.id) === String(e.id))
              if (it && e.lineId != null) it.lineId = e.lineId
            })
            ;(payload.layout.teams || []).forEach((t) => {
              itGeo['team-' + t.id] = { x: t.x, y: t.y, w: t.w, h: t.h }
              const it = this.itemList.find((x) => x.kind === 'team' && String(x.id) === String(t.id))
              if (it && t.lineId != null) it.lineId = t.lineId
            })
            this.wsGeo = wsGeo
            this.lnGeo = lnGeo
            this.itGeo = itGeo
            this.ensureAllPlaced()
          } else {
            this.autoLayout()
          }
        })
        .catch(() => this.autoLayout())
    },

    // ---- 排布 ----
    autoLayout() {
      const gap = 24
      const off = 24
      const CW = 620
      const CH = 440
      const cols = Math.max(1, Math.ceil(Math.sqrt(this.workshopList.length || 1)))
      const wsGeo = {}
      this.workshopList.forEach((ws, i) => {
        const c = i % cols
        const r = Math.floor(i / cols)
        wsGeo[ws.id] = { x: off + c * (CW + gap), y: off + r * (CH + gap), w: CW, h: CH }
      })
      this.wsGeo = wsGeo
      const lnGeo = {}
      const itGeo = {}
      this.workshopList.forEach((ws) => {
        const lines = this.linesOf(ws.id)
        const box = this.gridIn(lines.length, wsGeo[ws.id].w, 20, 260, 180)
        lines.forEach((ln, idx) => {
          const g = {
            x: box.ox + (idx % box.perRow) * (box.cw + box.gap),
            y: box.oy + Math.floor(idx / box.perRow) * (box.ch + box.gap),
            w: box.cw,
            h: box.ch
          }
          lnGeo[ln.id] = g
          const items = this.itemsOf(ln.id)
          const ib = this.gridIn(items.length, g.w, 8, 130, 64)
          items.forEach((it, j) => {
            itGeo[it.kind + '-' + it.id] = {
              x: ib.ox + (j % ib.perRow) * (ib.cw + ib.gap),
              y: ib.oy + Math.floor(j / ib.perRow) * (ib.ch + ib.gap),
              w: ib.cw,
              h: ib.ch
            }
          })
        })
      })
      this.lnGeo = lnGeo
      this.itGeo = itGeo
      this.fitCanvas()
    },
    gridIn(count, bodyW, pad, minW, minH) {
      const gap = 12
      const head = LN_HEAD_H
      const usable = Math.max(120, bodyW - pad * 2)
      const perRow = Math.max(1, Math.floor((usable + gap) / (minW + gap)))
      const cw = Math.max(minW, Math.floor((usable - gap * (perRow - 1)) / perRow))
      return { ox: pad, oy: head + pad, gap, cw, ch: minH, perRow }
    },
    ensureAllPlaced() {
      this.purgeStale()
      const firstWs = this.workshopList.length ? this.workshopList[0].id : null
      this.workshopList.forEach((ws) => {
        if (!this.wsGeo[ws.id]) {
          const placed = Object.keys(this.wsGeo).map((k) => this.wsGeo[k])
          let y = 24
          placed.forEach((p) => (y = Math.max(y, p.y + p.h + 24)))
          this.$set(this.wsGeo, ws.id, { x: 24, y, w: 620, h: 440 })
        }
      })
      this.lineList.forEach((ln) => {
        if (ln.workshopId == null) ln.workshopId = firstWs
        if (!this.lnGeo[ln.id]) {
          const ws = this.wsGeo[ln.workshopId] || { w: 620, h: 440 }
          const box = this.gridIn(1, ws.w, 20, 260, 180)
          this.$set(this.lnGeo, ln.id, { x: box.ox, y: box.oy, w: box.cw, h: box.ch })
        }
      })
      const firstLine = this.lineList.length ? this.lineList[0].id : null
      this.itemList.forEach((it) => {
        if (it.lineId == null) it.lineId = firstLine
        const key = it.kind + '-' + it.id
        if (!this.itGeo[key]) {
          const ln = this.lnGeo[it.lineId] || { w: 260, h: 180 }
          const box = this.gridIn(1, ln.w, 8, 130, 64)
          this.$set(this.itGeo, key, { x: box.ox, y: box.oy, w: box.cw, h: box.ch })
        }
      })
      this.fitCanvas()
    },
    purgeStale() {
      const wsIds = this.workshopList.map((w) => String(w.id))
      Object.keys(this.wsGeo).forEach((id) => wsIds.indexOf(String(id)) === -1 && this.$delete(this.wsGeo, id))
      const lnIds = this.lineList.map((l) => String(l.id))
      Object.keys(this.lnGeo).forEach((id) => lnIds.indexOf(String(id)) === -1 && this.$delete(this.lnGeo, id))
      const keys = this.itemList.map((i) => i.kind + '-' + i.id)
      Object.keys(this.itGeo).forEach((k) => keys.indexOf(k) === -1 && this.$delete(this.itGeo, k))
    },
    fitCanvas() {
      let maxX = 1200
      let maxY = 800
      Object.keys(this.wsGeo).forEach((id) => {
        const g = this.wsGeo[id]
        maxX = Math.max(maxX, g.x + g.w + 40)
        maxY = Math.max(maxY, g.y + g.h + 40)
      })
      this.canvas.w = maxX
      this.canvas.h = maxY
    },

    // ---- 拖拽/缩放 ----
    startMove(type, id, e, kind) {
      e.preventDefault()
      this.selected = type === 'it' ? { type, id, kind } : { type, id }
      const geo = type === 'ws' ? this.wsGeo[id] : type === 'ln' ? this.lnGeo[id] : this.itGeo[kind + '-' + id]
      if (!geo) return
      this.drag = { mode: 'move', type, id, kind, startX: e.clientX, startY: e.clientY, orig: { x: geo.x, y: geo.y } }
    },
    startResize(type, id, e, kind) {
      e.preventDefault()
      this.selected = type === 'it' ? { type, id, kind } : { type, id }
      const geo = type === 'ws' ? this.wsGeo[id] : type === 'ln' ? this.lnGeo[id] : this.itGeo[kind + '-' + id]
      if (!geo) return
      this.drag = { mode: 'resize', type, id, kind, startX: e.clientX, startY: e.clientY, orig: { w: geo.w, h: geo.h } }
    },
    onMove(e) {
      if (!this.drag) return
      const dx = (e.clientX - this.drag.startX) / this.zoom
      const dy = (e.clientY - this.drag.startY) / this.zoom
      const { mode, type, id, kind, orig } = this.drag
      if (type === 'ws') {
        const g = this.wsGeo[id]
        if (mode === 'move') {
          g.x = this.snap(Math.max(0, orig.x + dx))
          g.y = this.snap(Math.max(0, orig.y + dy))
        } else {
          g.w = this.snap(Math.max(300, orig.w + dx))
          g.h = this.snap(Math.max(200, orig.h + dy))
        }
        this.fitCanvas()
      } else if (type === 'ln') {
        const g = this.lnGeo[id]
        const line = this.lineList.find((l) => String(l.id) === String(id))
        const wg = this.wsGeo[line.workshopId] || { w: 620, h: 440 }
        const bodyW = wg.w
        const bodyH = wg.h - WS_HEAD_H
        if (mode === 'move') {
          g.x = this.snap(Math.max(0, Math.min(orig.x + dx, bodyW - g.w)))
          g.y = this.snap(Math.max(0, Math.min(orig.y + dy, bodyH - g.h)))
        } else {
          g.w = this.snap(Math.max(140, Math.min(orig.w + dx, bodyW - g.x)))
          g.h = this.snap(Math.max(90, Math.min(orig.h + dy, bodyH - g.y)))
        }
      } else {
        const key = kind + '-' + id
        const g = this.itGeo[key]
        const item = this.itemList.find((i) => i.kind === kind && String(i.id) === String(id))
        const lg = this.lnGeo[item.lineId] || { w: 260, h: 180 }
        const bodyW = lg.w
        const bodyH = lg.h - LN_HEAD_H
        if (mode === 'move') {
          g.x = this.snap(Math.max(0, Math.min(orig.x + dx, bodyW - g.w)))
          g.y = this.snap(Math.max(0, Math.min(orig.y + dy, bodyH - g.h)))
        } else {
          g.w = this.snap(Math.max(80, Math.min(orig.w + dx, bodyW - g.x)))
          g.h = this.snap(Math.max(44, Math.min(orig.h + dy, bodyH - g.y)))
        }
      }
    },
    onUp() {
      if (this.drag && this.drag.type === 'ln' && this.drag.mode === 'move') this.reparentLine(this.drag.id)
      if (this.drag && this.drag.type === 'ws') this.fitCanvas()
      this.drag = null
    },
    // 产线拖到别的车间上方则改归属（纯布局层，不动底层数据）
    reparentLine(lineId) {
      const line = this.lineList.find((l) => String(l.id) === String(lineId))
      const lg = this.lnGeo[lineId]
      if (!line || !lg) return
      const curWs = this.wsGeo[line.workshopId] || { x: 0, y: 0, w: 0, h: 0 }
      const cx = curWs.x + lg.x + lg.w / 2
      const cy = curWs.y + WS_HEAD_H + lg.y + lg.h / 2
      const target = this.workshopList.find((w) => {
        if (String(w.id) === String(line.workshopId)) return false
        const g = this.wsGeo[w.id]
        return g && cx >= g.x && cx <= g.x + g.w && cy >= g.y + WS_HEAD_H && cy <= g.y + g.h
      })
      if (target) {
        const tg = this.wsGeo[target.id]
        line.workshopId = target.id
        lg.x = this.snap(Math.max(0, cx - tg.x - lg.w / 2))
        lg.y = this.snap(Math.max(0, cy - tg.y - WS_HEAD_H - lg.h / 2))
      }
    },
    snap(v) {
      return Math.round(v / GRID) * GRID
    },

    // ---- 保存 ----
    saveLayout() {
      this.saving = true
      const workshops = this.workshopList.filter((w) => this.wsGeo[w.id]).map((w) => Object.assign({ id: w.id }, this.wsGeo[w.id]))
      const lines = this.lineList
        .filter((l) => this.lnGeo[l.id])
        .map((l) => {
          const g = this.lnGeo[l.id]
          return { id: l.id, workshopId: l.workshopId, x: g.x, y: g.y, w: g.w, h: g.h }
        })
      const equipment = []
      const teams = []
      this.itemList.forEach((it) => {
        const g = this.itGeo[it.kind + '-' + it.id]
        if (!g) return
        const row = { id: it.id, lineId: it.lineId, x: g.x, y: g.y, w: g.w, h: g.h }
        if (it.kind === 'eq') equipment.push(row)
        else teams.push(row)
      })
      this.$api({
        method: 'post',
        url: '/admin/gcvip/productionLayout/save',
        data: { scope: 'org', layout: { workshops, lines, equipment, teams }, canvas_w: this.canvas.w, canvas_h: this.canvas.h }
      })
        .then((res) => {
          if (res.data.state !== 'fail') this.$message.success('布局已保存')
        })
        .catch(() => {})
        .then(() => (this.saving = false))
    },

    // ---- 辅助 ----
    linesOf(wsId) {
      return this.lineList.filter((l) => String(l.workshopId) === String(wsId))
    },
    itemsOf(lnId) {
      return this.itemList.filter((i) => String(i.lineId) === String(lnId))
    },
    eqCount(lnId) {
      return this.itemsOf(lnId).filter((i) => i.kind === 'eq').length
    },
    teamCount(lnId) {
      return this.itemsOf(lnId).filter((i) => i.kind === 'team').length
    },
    wsName(id) {
      const w = this.workshopList.find((x) => String(x.id) === String(id))
      return w ? w.name : '未分配'
    },
    wsStyle(id) {
      const g = this.wsGeo[id] || { x: 0, y: 0, w: 620, h: 440 }
      return { left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px' }
    },
    lnStyle(id) {
      const g = this.lnGeo[id] || { x: 20, y: 50, w: 260, h: 180 }
      return { left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px' }
    },
    itStyle(it) {
      const g = this.itGeo[it.kind + '-' + it.id] || { x: 8, y: 30, w: 130, h: 64 }
      return { left: g.x + 'px', top: g.y + 'px', width: g.w + 'px', height: g.h + 'px' }
    },
    setZoom(z) {
      this.zoom = Math.max(0.3, Math.min(2, Math.round(z * 100) / 100))
    },
    zoomIn() {
      this.setZoom(this.zoom + 0.1)
    },
    zoomOut() {
      this.setZoom(this.zoom - 0.1)
    },
    zoomReset() {
      this.zoom = 1
    },
    saveView() {
      try {
        localStorage.setItem('prod_org_view', JSON.stringify({ zoom: this.zoom }))
      } catch (e) {
        /* ignore */
      }
    },
    loadView() {
      try {
        const s = localStorage.getItem('prod_org_view')
        if (s) {
          const o = JSON.parse(s) || {}
          if (o.zoom) this.zoom = o.zoom
        }
      } catch (e) {
        /* ignore */
      }
    }
  }
}
</script>

<style lang="less" scoped>
.page-main {
  .page-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    .title {
      font-size: 15px;
      font-weight: bold;
    }
    .legend-item {
      margin-right: 10px;
      font-size: 12px;
      .legend-dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 2px;
        margin-right: 4px;
        vertical-align: middle;
      }
    }
  }
  .content {
    display: flex;
    .canvas-wrap {
      flex: 1;
      min-width: 0;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background: #c0c4cc;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: #909399;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background-color: #f7f8fa;
      background-image: linear-gradient(#e8eaef 1px, transparent 1px), linear-gradient(90deg, #e8eaef 1px, transparent 1px);
      background-size: 20px 20px;
      .canvas-sizer {
        position: relative;
      }
      .canvas {
        position: absolute;
        top: 0;
        left: 0;
        .canvas-empty {
          position: absolute;
          top: 40px;
          left: 40px;
          color: #909399;
        }
      }
    }
    .detail-panel {
      width: 260px;
      margin-left: 10px;
      padding: 10px;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background: #fff;
      overflow: auto;
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background: #c0c4cc;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      .detail-title {
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 6px;
      }
      .detail-sub {
        font-size: 12px;
        color: #606266;
        line-height: 20px;
      }
      .detail-divider {
        border-top: 1px dashed #dcdfe6;
        margin: 8px 0;
      }
      .detail-count {
        font-size: 12px;
        margin-bottom: 6px;
      }
      .detail-list {
        .detail-row {
          padding: 6px;
          border-bottom: 1px solid #f0f0f0;
          .dr-name {
            font-size: 13px;
          }
          .dr-info {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            .dr-qty {
              color: #e6a23c;
            }
          }
        }
      }
    }
  }
}

.ws-block {
  position: absolute;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #c8ccd4;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  &.active {
    border-color: #396bf6;
    box-shadow: 0 0 0 2px rgba(57, 107, 246, 0.25);
  }
  .ws-head {
    height: 28px;
    line-height: 28px;
    padding: 0 8px;
    background: #2f333b;
    color: #fff;
    border-radius: 6px 6px 0 0;
    cursor: move;
    white-space: nowrap;
    overflow: hidden;
    .ws-name {
      font-size: 13px;
    }
    .ws-code {
      float: right;
      font-size: 11px;
      color: #b7bcc7;
    }
  }
  .ws-body {
    position: absolute;
    top: 28px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
}

.ln-block {
  position: absolute;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-left: 4px solid #409eff;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &.active {
    box-shadow: 0 0 0 2px rgba(57, 107, 246, 0.35);
    border-color: #396bf6;
  }
  .ln-head {
    height: 24px;
    line-height: 24px;
    padding: 0 6px;
    background: #eaf2ff;
    color: #337;
    border-radius: 4px 4px 0 0;
    cursor: move;
    white-space: nowrap;
    overflow: hidden;
    .ln-name {
      font-size: 12px;
      font-weight: bold;
      color: #409eff;
    }
  }
  .ln-body {
    position: absolute;
    top: 24px;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
}

.it-box {
  position: absolute;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-left: 4px solid #909399;
  border-radius: 4px;
  padding: 3px 5px;
  cursor: move;
  overflow: hidden;
  font-size: 11px;
  &.is-eq {
    border-left-color: #67c23a;
  }
  &.is-team {
    border-left-color: #e6a23c;
  }
  &.active {
    box-shadow: 0 0 0 2px rgba(57, 107, 246, 0.35);
    border-color: #396bf6;
  }
  .it-name {
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .it-sub {
    color: #909399;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  right: 0;
  bottom: 0;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 40%, #396bf6 40%, #396bf6 55%, transparent 55%, transparent 70%, #396bf6 70%, #396bf6 85%, transparent 85%);
}
.ws-resize {
  z-index: 5;
}
.ln-resize {
  z-index: 4;
}
.it-resize {
  z-index: 3;
  width: 9px;
  height: 9px;
}
</style>
